import React from "react";
import { Box, Divider, MenuItem } from "@mui/material";
import CustomStyledMenu from "../../../../components/Menu/CustomStyledMenu";
import IconComponent from "../../../../components/Icon/IconComponent";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import { GREY, BRAND, BASE } from "../../../../utils/constants/colors";
import { innerLeftBorder } from "../../../../utils/helpers";
import { fontWeight, ICON_LABELS } from "../../../../utils/constants/UI";
import { STRINGS } from "../../strings";

/**
 * The Call and Email quick actions, as anchored dropdowns rather than centre-screen modals.
 *
 * Both used to open a CustomModal of tiles, so reaching a number took two clicks and the rep
 * never saw which number they were about to dial. Now the numbers themselves are the menu.
 *
 * The shell is CustomStyledMenu. The rows are MenuItem, because components/Menu/ has no row
 * component to import: it holds only CustomStyledMenu and CustomMenu, and CustomMenu maps a fixed
 * `items` array so it cannot carry a section header, a per-row pencil or a divider. Rather than
 * change a component five screens depend on, these rows borrow CustomMenu's own item look, which
 * is what the rest of the CRM's dropdowns already feel like: the brand-red inner left edge on
 * hover, and its 8px/10px padding.
 *
 * One list, headed by the contact's name, and NO separate company section. HubSpot can show a
 * company section because company phone numbers live on the company record; here every number
 * including `corporate_phone_number` is a field on the CONTACT, so a company header with its own
 * "Add phone number" under it would be writing to the contact while claiming otherwise, and would
 * render blank for every contact with no company name. The row's own label carries that meaning.
 */

/** Field name to the words a rep would use for it. Order is the dialling priority. */
const PHONE_FIELDS = [
  { key: "mobile_number", label: "Mobile" },
  { key: "direct_phone_number", label: "Direct Phone" },
  { key: "phone_number", label: "Phone" },
  { key: "corporate_phone_number", label: "Corporate Phone" },
];

/**
 * Pretty only when the value is unambiguous, otherwise verbatim. A stored number is shown even
 * when it looks like junk: hiding one produces "where did my number go", and RingCentral refuses
 * an unusable number with a message the dialog already displays.
 */
const prettyPhone = (raw) => {
  const s = String(raw || "").trim();
  const d = s.replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) {
    return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10 && !s.startsWith("+")) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return s;
};

/** CustomMenu's `defaultItem`, so a row here feels like a row anywhere else in the CRM. */
const houseRow = {
  padding: "8px 12px",
  gap: "10px",
  "&:hover": {
    backgroundColor: BASE.BACKGROUND_LIGHT,
    boxShadow: innerLeftBorder(BRAND.PRIMARY),
  },
};

const SectionHeader = ({ text }) => (
  <Box sx={{ padding: "10px 16px 6px 16px" }}>
    <TypographyInline
      size="sm"
      color={GREY.PRIMARY}
      fontWeight={fontWeight.SEMIBOLD}
      label={text}
      isEllipses
    />
  </Box>
);

/**
 * An add-something row. Reads as a link, so it uses the brand colour the rest of the app uses for
 * emphasised text. It was ACTIONS.DEFAULT at first, which is #F1F1F1: a surface token, not a text
 * one, so the row was very nearly invisible against white.
 */
const LinkRow = ({ text, onClick }) => (
  <MenuItem onClick={onClick} sx={houseRow}>
    <IconComponent color={BRAND.PRIMARY} fontSize="small" iconLabel={ICON_LABELS.ADD} />
    <TypographyInline
      size="sm"
      color={BRAND.PRIMARY}
      fontWeight={fontWeight.SEMIBOLD}
      label={text}
    />
  </MenuItem>
);

const QuietRow = ({ text, onClick, disabled = false }) => (
  <MenuItem onClick={onClick} disabled={disabled} sx={houseRow}>
    <TypographyInline
      size="sm"
      color={GREY.SECONDARY}
      fontWeight={fontWeight.MEDIUM}
      label={text}
    />
  </MenuItem>
);

/**
 * One dialable number. The row body dials; the pencil edits. The pencil stops propagation and
 * carries its own padding, because the misfire that matters is aiming for it and hitting the row.
 */
const NumberRow = ({ value, label, onDial, onEdit }) => (
  <MenuItem
    onClick={onDial}
    sx={{ ...houseRow, "&:hover .riq-edit": { opacity: 1 } }}
  >
    <IconComponent color={GREY.SECONDARY} fontSize="small" iconLabel={ICON_LABELS.CALL} />
    <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
      <TypographyInline
        size="sm"
        color={GREY.PRIMARY}
        fontWeight={fontWeight.REGULAR}
        label={`Call ${prettyPhone(value)}  (${label})`}
        isEllipses
      />
    </Box>
    <Box
      className="riq-edit"
      onClick={(e) => {
        e.stopPropagation();
        onEdit();
      }}
      sx={{
        flex: "0 0 auto",
        opacity: 0,
        display: "grid",
        placeItems: "center",
        width: "28px",
        height: "28px",
        borderRadius: "6px",
        transition: "opacity 0.12s ease-in-out",
        "&:hover": { background: BASE.BACKGROUND_LIGHT },
      }}
    >
      <IconComponent color={GREY.TERTIARY} fontSize="small" iconLabel={ICON_LABELS.EDIT} />
    </Box>
  </MenuItem>
);

export const CallNumberMenu = ({
  anchorEl,
  open,
  onClose,
  contactDetails,
  onDial,
  onCallOther,
  onLogCall,
  onEditNumbers,
}) => {
  const numbers = PHONE_FIELDS
    .map((f) => ({ ...f, value: contactDetails?.[f.key] }))
    .filter((f) => String(f.value || "").trim());

  const name = [contactDetails?.first_name, contactDetails?.last_name]
    .filter(Boolean)
    .join(" ");

  return (
    <CustomStyledMenu
      arrow
      isRightAlign
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      width="360px"
      marginTop="10px !important"
      marginLeft="0px !important"
    >
      <SectionHeader text={name || "This contact"} />

      {numbers.length ? (
        numbers.map((n) => (
          <NumberRow
            key={n.key}
            value={n.value}
            label={n.label}
            onDial={() => {
              onClose();
              onDial(n.value);
            }}
            onEdit={() => {
              onClose();
              onEditNumbers();
            }}
          />
        ))
      ) : (
        <Box sx={{ padding: "2px 16px 8px 16px" }}>
          <TypographyInline
            size="xs"
            color={GREY.TERTIARY}
            fontWeight={fontWeight.REGULAR}
            label="No phone number saved on this contact yet."
          />
        </Box>
      )}

      <LinkRow
        text="Add phone number"
        onClick={() => {
          onClose();
          onEditNumbers();
        }}
      />

      <Divider sx={{ margin: "6px 0" }} />

      {/* Kept because the number list would otherwise remove the ability to dial anything that is
          not already on the record, which the old modal allowed. */}
      <QuietRow
        text="Call another number"
        onClick={() => {
          onClose();
          onCallOther();
        }}
      />
      <QuietRow
        text="Log a call"
        onClick={() => {
          onClose();
          onLogCall();
        }}
      />
    </CustomStyledMenu>
  );
};

/**
 * Email is a SINGLE field on a contact, so there is nothing to choose between and no dropdown of
 * addresses to build. What this button always multiplexed is three actions, and those genuinely
 * are a menu. Composing to the one address is the primary row so that clicking the address does
 * the obvious thing.
 */
export const EmailActionMenu = ({
  anchorEl,
  open,
  onClose,
  email,
  onCompose,
  onLog,
  onEnroll,
  onAddEmail,
  actionsBlocked = false,
}) => (
  <CustomStyledMenu
    arrow
    isRightAlign
    anchorEl={anchorEl}
    open={open}
    onClose={onClose}
    width="360px"
    marginTop="10px !important"
    marginLeft="0px !important"
  >
    {email ? (
      <MenuItem
        onClick={() => {
          onClose();
          onCompose();
        }}
        disabled={actionsBlocked}
        sx={houseRow}
      >
        <IconComponent color={GREY.SECONDARY} fontSize="small" iconLabel={ICON_LABELS.EMAIL} />
        <TypographyInline
          size="sm"
          color={GREY.PRIMARY}
          fontWeight={fontWeight.REGULAR}
          // The tester reported the Compose Email button as "missing": the row was still here
          // and still wired to onCompose, but it had been relabelled to the address itself, so
          // nobody could find it by name. Say what the row DOES.
          label={STRINGS.COMPOSE_EMAIL}
          isEllipses
        />
      </MenuItem>
    ) : (
      <LinkRow
        text="Add email"
        onClick={() => {
          onClose();
          onAddEmail();
        }}
      />
    )}

    <Divider sx={{ margin: "6px 0" }} />

    <QuietRow
      text="Log an email"
      onClick={() => {
        onClose();
        onLog();
      }}
    />
    <QuietRow
      text="Enroll in sequence"
      disabled={actionsBlocked}
      onClick={() => {
        onClose();
        onEnroll();
      }}
    />
  </CustomStyledMenu>
);

/**
 * Meetings. Two actions, no record data to list, so this is a plain menu. It exists as a dropdown
 * only so all four quick actions behave the same way: a centre-screen modal to pick between two
 * words was never worth dimming the page for.
 */
export const MeetingActionMenu = ({ anchorEl, open, onClose, onLog, onCreate }) => (
  <CustomStyledMenu
    arrow
    isRightAlign
    anchorEl={anchorEl}
    open={open}
    onClose={onClose}
    width="300px"
    marginTop="10px !important"
    marginLeft="0px !important"
  >
    <MenuItem
      onClick={() => {
        onClose();
        onCreate();
      }}
      sx={houseRow}
    >
      <IconComponent color={GREY.SECONDARY} fontSize="small" iconLabel={ICON_LABELS.EVENT_NOTE} />
      <TypographyInline
        size="sm"
        color={GREY.PRIMARY}
        fontWeight={fontWeight.REGULAR}
        label="Schedule a meeting"
      />
    </MenuItem>

    <Divider sx={{ margin: "6px 0" }} />

    <QuietRow
      text="Log a meeting"
      onClick={() => {
        onClose();
        onLog();
      }}
    />
  </CustomStyledMenu>
);
